import React, { useState, useEffect } from 'react';
import { Button, message, Table, Upload, Progress, Input, Select, Tag, Tooltip, Typography } from 'antd';
import { UploadOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { TableColumnsType } from 'antd';
import { useLazyQuery, useMutation } from '@apollo/client';
import { GET_CUSTOMS_CLEARANCE_TASKS, QueryClearanceUploadTasksResponse, QueryClearanceUploadTasksVariables, UploadTaskType } from './graphql/queries/getCustomsClearanceTasks';
import { GET_TASK_DETAIL_DATA } from './graphql/queries/getTaskDetailData';
import { DOWNLOAD_TASK_DETAIL_EXCEL } from './graphql/queries/downloadTaskDetailExcel';
import { PROCESS_CLEARANCE_EXCEL } from './graphql/mutations/processClearanceExcel';
import { fileUpload } from 'utils/storageUtils';
import { useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../components/TablePaginationBar';

const { Search } = Input;
const { Option } = Select;

// 使用从GraphQL查询导入的类型
type UploadTask = UploadTaskType;

const CustomsClearancePage: React.FC = () => {
    const { t } = useTranslation();
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [currentTaskNo, setCurrentTaskNo] = useState<string | null>(null);
    const [filter, setFilter] = useState({
        taskNo: '',
        status: undefined as number | undefined,
    });
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });
    const [autoRefresh, setAutoRefresh] = useState(false);
    
    // 手动刷新数据
    const refreshData = () => {
        executeQuery({
            variables: {
                filter: {
                    taskNo: filter.taskNo || undefined,
                    status: filter.status,
                },
                page: pagination.current,
                pageSize: pagination.pageSize,
            },
        });
    };

    // GraphQL查询 - 使用懒加载查询，只在手动触发时执行
    const [executeQuery, { data: tasksData, loading, error: queryError }] = useLazyQuery<QueryClearanceUploadTasksResponse, QueryClearanceUploadTasksVariables>(GET_CUSTOMS_CLEARANCE_TASKS, {
        fetchPolicy: 'cache-and-network', // 确保数据更新
        notifyOnNetworkStatusChange: true, // 网络状态变化时通知
    });

    // 处理查询错误
    useEffect(() => {
        if (queryError) {
            message.error(t('central.customsClearance.messages.fetchTasksFailed', { message: queryError.message }));
        }
    }, [queryError, t]);

    // 轮询进度更新
    useEffect(() => {
        let intervalId: NodeJS.Timeout | null = null;
        let timeoutId: NodeJS.Timeout | null = null;
        
        console.log('轮询effect触发:', { uploading, currentTaskNo });
        
        if (uploading && currentTaskNo) {
            console.log('开始轮询任务进度:', currentTaskNo, 'uploading:', uploading);
            
            // 设置5分钟超时
            timeoutId = setTimeout(() => {
                console.log('轮询超时，停止轮询');
                setUploading(false);
                setCurrentTaskNo(null);
                setUploadProgress(100);
                message.warning(t('central.customsClearance.messages.processTimeout'));
                if (intervalId) {
                    clearInterval(intervalId);
                }
            }, 5 * 60 * 1000); // 5分钟
            
            intervalId = setInterval(async () => {
                try {
                    console.log('轮询任务状态...', { currentTaskNo, uploading });
                    const { data } = await executeQuery({
                        variables: {
                            filter: {
                                taskNo: filter.taskNo || undefined,
                                status: filter.status,
                            },
                            page: pagination.current,
                            pageSize: pagination.pageSize,
                        },
                    });
                    console.log('轮询返回的数据:', data);
                    if (data?.queryClearanceUploadTasks?.tasks) {
                        const currentTask = data.queryClearanceUploadTasks.tasks.find(
                            (task: any) => task.taskNo === currentTaskNo
                        );
                        
                        console.log('找到任务:', currentTask);
                        console.log('任务状态详情:', {
                            status: currentTask?.status,
                            totalCount: currentTask?.totalCount,
                            successCount: currentTask?.successCount,
                            failedCount: currentTask?.failedCount
                        });
                        
                        if (currentTask) {
                            const totalCount = currentTask.totalCount;
                            const processedCount = currentTask.successCount + currentTask.failedCount;
                            
                            console.log(`任务进度: ${processedCount}/${totalCount}, 状态: ${currentTask.status}`);
                            
                            if (totalCount > 0) {
                                // 改进进度计算逻辑
                                let progress = 0;
                                if (currentTask.status === 0) {
                                    // 待处理状态，显示10%
                                    progress = 10;
                                } else if (currentTask.status === 1) {
                                    // 部分失败状态，显示100%（任务已处理完成）
                                    progress = 100;
                                } else if (currentTask.status === 2 || currentTask.status === 3) {
                                    // 完成或失败状态，显示100%
                                    progress = 100;
                                }
                                
                                setUploadProgress(progress);
                                
                                // 如果任务完成，停止轮询
                                if (currentTask.status === 1 || currentTask.status === 2 || currentTask.status === 3) {
                                    console.log('任务完成，停止轮询', {
                                        status: currentTask.status,
                                        successCount: currentTask.successCount,
                                        failedCount: currentTask.failedCount
                                    });
                                    setUploading(false);
                                    setCurrentTaskNo(null);
                                    setUploadProgress(100);
                                    
                                    // 清理定时器
                                    if (intervalId) {
                                        clearInterval(intervalId);
                                    }
                                    if (timeoutId) {
                                        clearTimeout(timeoutId);
                                    }
                                    
                                    // 刷新页面数据
                                    console.log('开始刷新页面数据...');
                                    await executeQuery({
                                        variables: {
                                            filter: {
                                                taskNo: filter.taskNo || undefined,
                                                status: filter.status,
                                            },
                                            page: pagination.current,
                                            pageSize: pagination.pageSize,
                                        },
                                    });
                                    console.log('页面数据刷新完成');
                                    
                                    if (currentTask.status === 2) {
                                        message.success(t('central.customsClearance.messages.processCompleted', { successCount: currentTask.successCount, failedCount: currentTask.failedCount }));
                                    } else if (currentTask.status === 1) {
                                        message.warning(t('central.customsClearance.messages.processCompletedPartial', { successCount: currentTask.successCount, failedCount: currentTask.failedCount }));
                                    } else {
                                        message.error(t('central.customsClearance.messages.processFailed', { successCount: currentTask.successCount, failedCount: currentTask.failedCount }));
                                    }
                                }
                            }
                        } else {
                            console.log('未找到任务，可能已完成');
                        }
                    }
                } catch (error) {
                    console.error('轮询进度失败:', error);
                }
            }, 2000); // 每2秒轮询一次
        }
        
        return () => {
            if (intervalId) {
                console.log('清理轮询定时器');
                clearInterval(intervalId);
            }
            if (timeoutId) {
                console.log('清理超时定时器');
                clearTimeout(timeoutId);
            }
        };
    }, [uploading, currentTaskNo, filter, pagination]);

    // 自动刷新功能
    useEffect(() => {
        let intervalId: NodeJS.Timeout | null = null;
        
        if (autoRefresh) {
            intervalId = setInterval(() => {
                refreshData();
            }, 5000); // 5秒自动刷新
        }
        
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [autoRefresh, filter, pagination]);

    // 页面初始化时执行一次查询
    useEffect(() => {
        executeQuery({
            variables: {
                filter: {
                    taskNo: undefined,
                    status: undefined,
                },
                page: 1,
                pageSize: pagination.pageSize,
            },
        });
    }, []); // 只在组件挂载时执行一次

    const [downloadTaskDetailExcel] = useMutation(DOWNLOAD_TASK_DETAIL_EXCEL);
    const [processClearanceExcel] = useMutation(PROCESS_CLEARANCE_EXCEL);

    // 处理文件上传
    const handleUpload = async (file: File) => {
        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.csv')) {
            message.error(t('central.customsClearance.upload.invalidFileType'));
            return false;
        }

        setUploading(true);
        setUploadProgress(0);
        setCurrentTaskNo(null);

        try {
            console.log('开始GraphQL文件上传流程');
            console.log('文件信息:', {
                name: file.name,
                size: file.size,
                type: file.type
            });

            // 步骤1: 使用现有工具上传文件到存储
            console.log('步骤1: 上传文件到存储');
            setUploadProgress(30);
            
            const { objectKey } = await fileUpload(file, 'customs-clearance');
            console.log('文件上传成功，objectKey:', objectKey);

            // 步骤2: 调用GraphQL mutation处理文件
            console.log('步骤2: 触发后端处理');
            setUploadProgress(60);
            
            const processResponse = await processClearanceExcel({
                variables: {
                    input: {
                        objectKey: objectKey,
                        filename: file.name,
                    },
                },
            });

            const { taskNo, totalCount } = processResponse.data.processClearanceExcel;
            console.log('后端处理启动成功:', { taskNo, totalCount });

            setCurrentTaskNo(taskNo);
            setUploadProgress(100);
            
            message.success(t('central.customsClearance.upload.uploadSuccess', { taskNo }));
            
        } catch (error: any) {
            console.error('文件上传失败:', error);
            message.error(t('central.customsClearance.upload.uploadFailed', { message: error.message }));
            setUploading(false);
            setUploadProgress(0);
            setCurrentTaskNo(null);
        }
        
        return false; // 阻止默认上传行为
    };

    // 处理筛选
    const handleFilter = (values: any) => {
        setFilter(values);
        setPagination(prev => ({ ...prev, current: 1 }));
        
        // 执行查询
        executeQuery({
            variables: {
                filter: {
                    taskNo: values.taskNo || undefined,
                    status: values.status,
                },
                page: 1,
                pageSize: pagination.pageSize,
            },
        });
    };

    // 处理表格变化
    const handleTableChange = (newPagination: any) => {
        setPagination(newPagination);
        
        // 执行分页查询
        executeQuery({
            variables: {
                filter: {
                    taskNo: filter.taskNo || undefined,
                    status: filter.status,
                },
                page: newPagination.current,
                pageSize: newPagination.pageSize,
            },
        });
    };

    // 下载明细
    const handleDownloadDetail = async (taskId: number, taskNo: string) => {
        try {
            message.loading(t('central.customsClearance.messages.generatingFile'), 0);
            
            const { data } = await downloadTaskDetailExcel({
                variables: { taskId: taskId }
            });

            if (data?.downloadUploadTaskDetail) {
                // 创建下载链接
                const downloadUrl = data.downloadUploadTaskDetail;
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = `customs_clearance_task_detail_${taskNo}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                message.destroy();
                message.success(t('central.customsClearance.messages.downloadSuccess'));
            } else {
                message.destroy();
                message.error(t('central.customsClearance.messages.downloadFailed'));
            }
        } catch (error) {
            message.destroy();
            console.error('下载失败:', error);
            message.error(t('central.customsClearance.messages.downloadFailedWithMessage', { message: (error as Error).message }));
        }
    };

    // 获取状态标签
    const getStatusTag = (status: number) => {
        const statusMap = {
            0: { color: 'default', text: t('central.customsClearance.status.pending') },
            1: { color: 'warning', text: t('central.customsClearance.status.partialFailure') },
            2: { color: 'success', text: t('central.customsClearance.status.completed') },
            3: { color: 'error', text: t('central.customsClearance.status.failed') },
        };
        const statusInfo = statusMap[status as keyof typeof statusMap] || { color: 'default', text: t('central.customsClearance.status.unknown') };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
    };

    // 获取错误原因
    const getErrorReason = (errorCode: number) => {
        const errorMap = {
            0: t('central.customsClearance.errorReason.noError'),
            1: t('central.customsClearance.errorReason.dataFormatError'),
            2: t('central.customsClearance.errorReason.requiredFieldMissing'),
            3: t('central.customsClearance.errorReason.duplicateData'),
            99: t('central.customsClearance.errorReason.systemError'),
        };
        return errorMap[errorCode as keyof typeof errorMap] || t('central.customsClearance.errorReason.unknownError');
    };

    // 表格列定义
    const columns: TableColumnsType<UploadTask> = [
        {
            title: t('central.customsClearance.columns.taskNo'),
            dataIndex: 'taskNo',
            key: 'taskNo',
            width: 160,
            ellipsis: { showTitle: false },
            render: (taskNo: string) => (
                <Tooltip title={taskNo}>
                    <Typography.Text style={{ display: 'block' }} ellipsis>
                        {taskNo}
                    </Typography.Text>
                </Tooltip>
            ),
        },
        {
            title: t('central.customsClearance.columns.submitter'),
            dataIndex: 'submitter',
            key: 'submitter',
            width: 120,
        },
        {
            title: t('central.customsClearance.columns.totalCount'),
            dataIndex: 'totalCount',
            key: 'totalCount',
            width: 60,
        },
        {
            title: t('central.customsClearance.columns.taskStatus'),
            key: 'taskStatus',
            width: 80,
            render: (_, record) => (
                <div>
                    <div>{t('central.customsClearance.columns.success')}: {record.successCount}</div>
                    <div>{t('central.customsClearance.columns.failed')}: {record.failedCount}</div>
                </div>
            ),
        },
        {
            title: t('central.customsClearance.columns.status'),
            dataIndex: 'status',
            key: 'status',
            width: 80,
            render: (status) => getStatusTag(status),
        },
        {
            title: t('central.customsClearance.columns.errorReason'),
            dataIndex: 'errorReason',
            key: 'errorReason',
            width: 300,
            ellipsis: { showTitle: false },
            render: (errorReason: string | null) => {
                const value = errorReason || '-';
                if (value === '-') {
                    return '-';
                }
                return (
                    <Tooltip
                        placement="topLeft"
                        title={
                            <div
                                style={{
                                    maxWidth: 720,
                                    maxHeight: 360,
                                    overflow: 'auto',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}
                            >
                                {value}
                            </div>
                        }
                    >
                        <Typography.Text
                            style={{ display: 'block' }}
                            ellipsis
                        >
                            {value}
                        </Typography.Text>
                    </Tooltip>
                );
            },
        },
        {
            title: t('central.customsClearance.columns.createdAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 140,
        },
        {
            title: t('central.customsClearance.columns.action'),
            key: 'action',
            width: 70,
            render: (_, record) => (
                <Button
                    type="link"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadDetail(record.id, record.taskNo)}
                >
                    {t('central.customsClearance.columns.downloadDetail')}
                </Button>
            ),
        },
    ];

    const tasks = tasksData?.queryClearanceUploadTasks?.tasks || [];
    const pageInfo = tasksData?.queryClearanceUploadTasks;

    // 当查询结果返回时，更新分页信息
    useEffect(() => {
        if (pageInfo) {
            setPagination(prev => ({
                ...prev,
                current: pageInfo.page || prev.current,
                pageSize: pageInfo.pageSize || prev.pageSize,
                total: pageInfo.totalElements || prev.total,
            }));
        }
    }, [pageInfo]);

    const handleResetFilters = () => {
        setFilter({ taskNo: '', status: undefined });
        setPagination(prev => ({ ...prev, current: 1 }));
        // 重置后执行查询
        executeQuery({
            variables: {
                filter: {
                    taskNo: undefined,
                    status: undefined,
                },
                page: 1,
                pageSize: pagination.pageSize,
            },
        });
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            {/* 上传区域 */}
            <div className="mb-4 pt-4">
                <div className="text-lg font-bold mb-4">{t('central.customsClearance.upload.title')}</div>
                <div className="flex flex-col gap-4">
                    <Upload.Dragger
                        accept=".xlsx,.xls,.csv"
                        beforeUpload={handleUpload}
                        showUploadList={false}
                        disabled={uploading}
                    >
                        <p className="ant-upload-drag-icon">
                            <UploadOutlined />
                        </p>
                        <p className="ant-upload-text">
                            {uploading ? t('central.customsClearance.upload.uploading') : t('central.customsClearance.upload.dragText')}
                        </p>
                        <p className="ant-upload-hint">
                            {t('central.customsClearance.upload.supportedFormats')}
                        </p>
                        <div className="ant-upload-hint mt-2 p-3 bg-blue-50 rounded border border-blue-200">
                            <div className="text-sm font-medium text-blue-800 mb-2">{t('central.customsClearance.upload.formatRequirements')}</div>
                            <div className="text-xs text-blue-700">
                                <div className="mb-1">{t('central.customsClearance.upload.csvFormat')}</div>
                                <div className="mt-1">{t('central.customsClearance.upload.excelFormat')}</div>
                            </div>
                        </div>
                    </Upload.Dragger>

                    {uploading && (
                        <div className="w-full">
                            <Progress 
                                percent={uploadProgress} 
                                status="active" 
                                format={(percent) => `${percent}%`}
                            />
                            {currentTaskNo && (
                                <div className="text-sm text-gray-600 mt-2">
                                    {t('central.customsClearance.upload.taskProcessing', { taskNo: currentTaskNo })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* 筛选区域 */}
            <div className="mb-2 pl-0 py-2">
                <div className="grid grid-cols-2 gap-x-10 gap-y-4 items-start">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="!mr-2 !w-[60px] !min-w-0 !flex-shrink-0 !font-bold">{t('central.customsClearance.filters.taskNo')}</span>
                            <Search
                                placeholder={t('central.customsClearance.filters.taskNoPlaceholder') || undefined}
                                value={filter.taskNo}
                                onChange={(e) => setFilter(prev => ({ ...prev, taskNo: e.target.value }))}
                                onSearch={() => handleFilter(filter)}
                                enterButton={<SearchOutlined />}
                                className="flex-1"
                            />
                        </div>
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="!mr-2 !w-[60px] !min-w-0 !flex-shrink-0 !font-bold">{t('central.customsClearance.filters.status')}</span>
                            <Select
                                placeholder={t('central.customsClearance.filters.statusPlaceholder') || undefined}
                                value={filter.status}
                                onChange={(value) => setFilter(prev => ({ ...prev, status: value }))}
                                allowClear
                                className="flex-1"
                            >
                                <Option value={0}>{t('central.customsClearance.status.pending')}</Option>
                                <Option value={1}>{t('central.customsClearance.status.partialFailure')}</Option>
                                <Option value={2}>{t('central.customsClearance.status.completed')}</Option>
                                <Option value={3}>{t('central.customsClearance.status.failed')}</Option>
                            </Select>
                        </div>
                    </div>
                </div>
            </div>

            {/* 批量操作区域 */}
            <div className="flex justify-end items-center gap-2 mb-3 pl-0">
                <Button 
                    type="primary" 
                    onClick={() => handleFilter(filter)}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('central.customsClearance.filters.search')}</span>
                </Button>
                <Button 
                    onClick={handleResetFilters}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('central.customsClearance.filters.reset')}</span>
                </Button>
                <Button 
                    type={autoRefresh ? "primary" : "default"}
                    onClick={() => setAutoRefresh(!autoRefresh)}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{autoRefresh ? t('central.customsClearance.filters.disableAutoRefresh') : t('central.customsClearance.filters.enableAutoRefresh')}</span>
                </Button>
                <Button 
                    onClick={refreshData}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('central.customsClearance.filters.manualRefresh')}</span>
                </Button>
            </div>

            {/* 任务列表 */}
            <Table
                columns={columns}
                dataSource={tasks}
                loading={loading}
                rowKey="id"
                pagination={false}
                scroll={{ x: 1200 }}
                size="middle"
                tableLayout="fixed"
             className="mb-4"/>

            {/* 分页 */}
            <TablePaginationBar
                total={pageInfo?.totalElements || pagination.total}
                current={pageInfo?.page || pagination.current}
                pageSize={pageInfo?.pageSize || pagination.pageSize}
                onChange={(page, size) => handleTableChange({ current: page, pageSize: size })}
            />
        </div>
    );
};

export default CustomsClearancePage;
